import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import ApiService from '../services/api';
import logger from '../utils/logger';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user: authUser, isAuthenticated } = useAuth();
  const [benefits, setBenefits] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initializeMockData = useCallback(() => {
    // Mock benefits data
    const mockBenefits = [
      {
        id: 1,
        name: "TRICARE Prime",
        status: "Active",
        coverage: "Self + Family",
        expiry_date: "2025-12-31",
        type: "health",
        ai_recommendation: "Your family coverage is optimal. AI suggests scheduling annual wellness checkups."
      },
      {
        id: 2,
        name: "GI Bill Benefits",
        status: "Available",
        remaining_months: 36,
        transferable: true,
        type: "education",
        ai_recommendation: "Consider transferring benefits to spouse for maximum family value."
      },
      {
        id: 3,
        name: "SGLI Life Insurance",
        status: "Active",
        coverage: "$400,000",
        premium: "$27/month",
        type: "insurance",
        ai_recommendation: "Coverage amount is adequate for your current family situation."
      },
      {
        id: 4,
        name: "VA Home Loan",
        status: "Eligible",
        used_amount: "$0",
        available_amount: "No Limit",
        type: "housing",
        ai_recommendation: "AI detected you're renting. Consider using VA loan benefit for home purchase."
      }
    ];

    const mockDocuments = [
      {
        id: 1,
        name: "DD Form 214",
        type: "Service Record",
        upload_date: "2024-03-15T00:00:00Z",
        status: "Verified",
        ai_processed: true,
        summary: "Honorable discharge documentation processed by AI"
      },
      {
        id: 2,
        name: "Medical Records",
        type: "Health",
        upload_date: "2024-07-10T00:00:00Z",
        status: "Processing",
        ai_processed: false,
        summary: "AI analyzing medical history for benefit recommendations"
      },
      {
        id: 3,
        name: "Marriage Certificate",
        type: "Family",
        upload_date: "2024-01-20T00:00:00Z",
        status: "Verified",
        ai_processed: true,
        summary: "Family status updated, dependent benefits activated"
      }
    ];

    const mockNotifications = [
      {
        id: 1,
        title: "AI Benefit Analysis Complete",
        message: "Your benefits have been analyzed. 3 optimization recommendations available.",
        type: "ai",
        read: false,
        timestamp: "2024-07-15T10:30:00Z"
      },
      {
        id: 2,
        title: "ID Card Expiration Reminder",
        message: "Your military ID expires in 30 days. AI has pre-filled renewal forms.",
        type: "reminder",
        read: false,
        timestamp: "2024-07-14T14:20:00Z"
      },
      {
        id: 3,
        title: "TRICARE Update",
        message: "Your TRICARE enrollment has been automatically renewed.",
        type: "update",
        read: true,
        timestamp: "2024-07-13T09:15:00Z"
      }
    ];

    const mockAIInsights = {
      benefit_optimization_score: 85,
      recommendations: [
        "Transfer 18 months of GI Bill to spouse for tax advantages",
        "Increase SGLI coverage based on recent salary increase",
        "Schedule preventive care to maximize TRICARE benefits"
      ],
      processed_documents: 12,
      pending_documents: 2,
      insights: [
        "All service records are complete and verified",
        "Medical records show eligibility for additional VA benefits",
        "Family documentation allows for dependent coverage optimization"
      ],
      upcoming_deadlines: [
        { task: "Annual TRICARE enrollment", dueDate: "2024-12-01", priority: "high" },
        { task: "Security clearance renewal", dueDate: "2025-03-15", priority: "medium" },
        { task: "Dependent ID renewal", dueDate: "2024-09-30", priority: "medium" }
      ]
    };

    setBenefits(mockBenefits);
    setDocuments(mockDocuments);
    setNotifications(mockNotifications);
    setAiInsights(mockAIInsights);
  }, []);

  const loadUserData = useCallback(async (userId) => {
    try {
      setLoading(true);
      
      const [
        userBenefits,
        userDocuments,
        userNotifications,
        insights
      ] = await Promise.all([
        ApiService.getUserBenefits(userId).catch(() => []),
        ApiService.getUserDocuments(userId).catch(() => []),
        ApiService.getUserNotifications(userId).catch(() => []),
        ApiService.getUserInsights(userId).catch(() => null)
      ]);

      setBenefits(Array.isArray(userBenefits) ? userBenefits : []);
      setDocuments(Array.isArray(userDocuments) ? userDocuments : []);
      setNotifications(Array.isArray(userNotifications) ? userNotifications : []);
      setAiInsights(insights);

    } catch (error) {
      logger.error('Error loading user data:', error);
      initializeMockData();
    } finally {
      setLoading(false);
    }
  }, [initializeMockData]);

  const refreshData = useCallback(() => {
    if (authUser && isAuthenticated) {
      loadUserData(authUser.id);
    }
  }, [authUser, isAuthenticated, loadUserData]);

  const refreshInsights = useCallback(async () => {
    if (!authUser) return;
    
    try {
      const insights = await ApiService.generateInsights(authUser.id);
      setAiInsights(insights);
    } catch (error) {
      logger.error('Error refreshing insights:', error);
    }
  }, [authUser]);

  // Load data when user authenticates
  useEffect(() => {
    if (authUser && isAuthenticated) {
      loadUserData(authUser.id);
    } else {
      // Clear data when user logs out
      setBenefits([]);
      setDocuments([]);
      setNotifications([]);
      setAiInsights(null);
    }
  }, [authUser, isAuthenticated, loadUserData]);

  const value = {
    user: authUser,
    benefits,
    documents,
    notifications,
    aiInsights,
    loading,
    error,
    refreshData,
    refreshInsights,
    setBenefits,
    setDocuments,
    setNotifications,
    setAiInsights
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};